/**
 * API Client for Insight Hub v2
 *
 * Handles all HTTP communication with the backend API.
 * Includes automatic JWT token injection, organization header, and 401 handling.
 */
import axios, { AxiosInstance, AxiosError } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

class ApiClient {
  private client: AxiosInstance;
  private currentOrganizationId: string | null = null;
  private currentProductId: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 second timeout
    });

    // Load organization ID and product ID from localStorage on initialization
    this.currentOrganizationId = localStorage.getItem('currentOrganizationId');
    this.currentProductId = localStorage.getItem('currentProductId');

    // Add auth token, organization ID, and product ID to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add X-Organization-ID header if organization is set (but don't override if already provided)
      if (!config.headers['X-Organization-ID']) {
        const orgId = this.getCurrentOrganization();
        if (orgId) {
          config.headers['X-Organization-ID'] = orgId;
          console.log('[API] Adding X-Organization-ID header:', orgId);
        } else {
          console.warn('[API] No organization ID found in localStorage!');
        }
      } else {
        console.log('[API] Using provided X-Organization-ID header:', config.headers['X-Organization-ID']);
      }

      // Add X-Product-ID header if product is set
      const productId = this.getCurrentProduct();
      if (productId) {
        config.headers['X-Product-ID'] = productId;
        console.log('[API] Adding X-Product-ID header:', productId);
      }

      return config;
    });

    // Handle 401 errors (unauthorized)
    this.client.interceptors.response.use(
      (response: any) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Clear token and redirect to login
          localStorage.removeItem('access_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Set the current organization ID
   * @param orgId - Organization ID to set as current
   */
  setCurrentOrganization(orgId: string): void {
    console.log('[API] setCurrentOrganization called with:', orgId);
    this.currentOrganizationId = orgId;
    localStorage.setItem('currentOrganizationId', orgId);
    console.log('[API] Saved to localStorage. Current value:', localStorage.getItem('currentOrganizationId'));
  }

  /**
   * Get the current organization ID
   * @returns Current organization ID or null
   */
  getCurrentOrganization(): string | null {
    if (!this.currentOrganizationId) {
      this.currentOrganizationId = localStorage.getItem('currentOrganizationId');
    }
    return this.currentOrganizationId;
  }

  /**
   * Clear the current organization
   */
  clearCurrentOrganization(): void {
    this.currentOrganizationId = null;
    localStorage.removeItem('currentOrganizationId');
    // Also clear product when organization is cleared
    this.clearCurrentProduct();
  }

  /**
   * Set the current product ID
   * @param productId - Product ID to set as current
   */
  setCurrentProduct(productId: string): void {
    console.log('[API] setCurrentProduct called with:', productId);
    this.currentProductId = productId;
    localStorage.setItem('currentProductId', productId);
    console.log('[API] Saved to localStorage. Current value:', localStorage.getItem('currentProductId'));
  }

  /**
   * Get the current product ID
   * @returns Current product ID or null
   */
  getCurrentProduct(): string | null {
    if (!this.currentProductId) {
      this.currentProductId = localStorage.getItem('currentProductId');
    }
    return this.currentProductId;
  }

  /**
   * Clear the current product
   */
  clearCurrentProduct(): void {
    this.currentProductId = null;
    localStorage.removeItem('currentProductId');
  }

  async get<T>(url: string, params?: any, config?: { headers?: Record<string, string>; timeout?: number }): Promise<T> {
    const response = await this.client.get<T>(url, { params, ...config });
    return response.data;
  }

  async post<T>(url: string, data?: any, params?: any, config?: { timeout?: number }): Promise<T> {
    const response = await this.client.post<T>(url, data, { params, ...config });
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: { headers?: Record<string, string> }): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: { data?: any }): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export const api = new ApiClient();
